import React, { useState } from 'react';
import axios from 'axios';
import { formatCompensation } from '../utils/compensation';

interface JobData {
  type: string;
  job_id: number;
  invite_id?: number;
  temp_request_id?: number;
  title: string;
  company: string;
  url: string;
  salary?: string | number;
  min_compensation?: string | number;
  compensation_type?: string;
  location?: string;
  position_type?: string;
  benefits?: string[];
  description_excerpt?: string;
  posted_at?: string;
  sender_profile_id?: number;
}

const BRAND_BLUE = '#0f4b89';

const cardStyles: Record<string, React.CSSProperties> = {
  card: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(15, 75, 137, 0.08)',
    maxWidth: 440,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px 16px 0 16px',
  },
  iconBadge: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: 10,
    background: 'rgba(15, 75, 137, 0.08)',
    color: BRAND_BLUE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
  },
  title: {
    fontWeight: 700,
    fontSize: 15,
    lineHeight: 1.3,
    color: '#111827',
    margin: 0,
  },
  company: { fontSize: 13, color: '#6b7280', marginTop: 2 },
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px 14px',
    padding: '10px 16px 0 16px',
    fontSize: 13,
    color: '#374151',
  },
  metaItem: { display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' },
  benefits: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    padding: '10px 16px 0 16px',
  },
  benefitPill: {
    fontSize: 11,
    fontWeight: 600,
    color: BRAND_BLUE,
    background: 'rgba(15, 75, 137, 0.07)',
    border: '1px solid rgba(15, 75, 137, 0.15)',
    borderRadius: 999,
    padding: '2px 9px',
  },
  excerpt: {
    padding: '10px 16px 0 16px',
    fontSize: 13,
    color: '#4b5563',
    lineHeight: 1.45,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
  },
  footer: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    padding: '14px 16px 14px 16px',
    marginTop: 12,
    borderTop: '1px solid #f3f4f6',
    background: '#fafbfc',
  },
};

const JobWidget: React.FC<{ data: JobData; isProfessional?: boolean }> = ({ data, isProfessional }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  // On mount, fetch the single invite so applied persists across reloads
  React.useEffect(() => {
    let cancelled = false;
    const fetchInvite = async () => {
      if (!data?.invite_id) return;
      try {
        const resp = await fetch(`/api/v1/application_invites/${encodeURIComponent(String(data.invite_id))}`, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!resp.ok) return;
        const body = await resp.json().catch(() => null);
        const invite = body?.application_invite || body;
        if (!cancelled && invite) setApplied(Boolean(invite.applied));
      } catch (err) {
        // ignore errors silently
      }
    };

    fetchInvite();
    return () => { cancelled = true; };
  }, [data?.invite_id]);

  const handleApply = async () => {
    if (!data.invite_id) return;
    setLoading(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      await axios.post(
        `/api/v1/application_invites/${data.invite_id}/apply`,
        {},
        { headers: { 'X-CSRF-Token': csrfToken, 'X-Requested-With': 'XMLHttpRequest' } }
      );
      setApplied(true);
      setError(null);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to apply');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTemp = async () => {
    if (!data.temp_request_id) return;
    setLoading(true);
    try {
      await axios.patch(`/api/v1/employer/temp_requests/${data.temp_request_id}/accept`);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to accept');
    } finally {
      setLoading(false);
    }
  };

  const compensation = formatCompensation(data);
  const benefits = (data.benefits || []).slice(0, 6);

  return (
    <div style={cardStyles.card} data-testid="job-widget">
      <div style={cardStyles.header}>
        <div style={cardStyles.iconBadge} aria-hidden="true">
          <i className="bi bi-briefcase-fill" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={cardStyles.title}>
            <a href={data.url} target="_blank" rel="noopener noreferrer"
               style={{ color: 'inherit', textDecoration: 'none' }}>
              {data.title}
            </a>
          </div>
          {data.company && <div style={cardStyles.company}>{data.company}</div>}
        </div>
      </div>

      {(data.location || data.position_type || compensation) && (
        <div style={cardStyles.metaRow}>
          {data.location && (
            <span style={cardStyles.metaItem}>
              <i className="bi bi-geo-alt" style={{ color: '#9ca3af' }} /> {data.location}
            </span>
          )}
          {data.position_type && (
            <span style={cardStyles.metaItem}>
              <i className="bi bi-clock" style={{ color: '#9ca3af' }} /> {data.position_type}
            </span>
          )}
          {compensation && (
            <span style={{ ...cardStyles.metaItem, color: BRAND_BLUE, fontWeight: 600 }}>
              <i className="bi bi-cash-stack" /> {compensation}
            </span>
          )}
        </div>
      )}

      {benefits.length > 0 && (
        <div style={cardStyles.benefits}>
          {benefits.map((benefit) => (
            <span key={benefit} style={cardStyles.benefitPill}>{benefit}</span>
          ))}
        </div>
      )}

      {data.description_excerpt && (
        <div style={cardStyles.excerpt}>{data.description_excerpt}</div>
      )}

      <div style={cardStyles.footer}>
        {data.invite_id && isProfessional && (
          applied ? (
            <button className="btn btn-sm btn-success" disabled>
              <i className="bi bi-check-lg me-1" />Applied
            </button>
          ) : (
            <button className="btn btn-sm btn-primary px-3" onClick={handleApply} disabled={loading}>
              {loading ? 'Applying…' : 'Apply now'}
            </button>
          )
        )}
        {data.temp_request_id && (
          <button className="btn btn-sm btn-primary" onClick={handleAcceptTemp} disabled={loading}>
            {loading ? 'Accepting…' : 'Accept Temp'}
          </button>
        )}
        <a className="btn btn-sm btn-outline-secondary" href={data.url} target="_blank" rel="noopener noreferrer">
          View job
        </a>
      </div>

      {error && <div style={{ color: '#dc2626', padding: '0 16px 12px 16px', fontSize: 13 }}>{error}</div>}
    </div>
  );
};

export default JobWidget;
